import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { BellRing, ArrowRight, Clock } from "lucide-react";

const timeLeft = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Launching soon";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days > 30) return new Date(iso).toLocaleDateString(undefined, { dateStyle: "long" });
  if (days > 0) return `${days} day${days > 1 ? "s" : ""} to go`;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  return `${hours} hour${hours !== 1 ? "s" : ""} to go`;
};

const ComingSoon = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Coming soon — Dream Home Engineers";
    api.get("/plans/coming-soon").then((r) => setPlans(r.data)).catch(() => setPlans([])).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-3xl">
          <div className="overline text-accent flex items-center gap-2"><BellRing size={14} /> Coming soon</div>
          <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95] tracking-tighter">On the drafting table.</h1>
          <p className="mt-6 text-lg text-muted-foreground">
            A glimpse at the plans currently in design review. Add yourself to the notification list for any plan and we&apos;ll email you the instant it goes live.
          </p>
        </div>

        {loading && <div className="mt-20 text-muted-foreground" data-testid="coming-soon-loading">Loading upcoming plans…</div>}

        {!loading && plans.length === 0 && (
          <div className="mt-20 border border-border p-16 text-center" data-testid="coming-soon-empty">
            <BellRing className="mx-auto text-muted-foreground mb-4" />
            <h3 className="font-display text-2xl">No upcoming plans right now.</h3>
            <p className="text-muted-foreground mt-2 text-sm">Check back soon — we publish new plans every fortnight.</p>
            <Link to="/plans" className="inline-block mt-6 border border-accent text-accent px-6 py-3 hover:bg-accent hover:text-accent-foreground transition-colors">Browse published plans</Link>
          </div>
        )}

        {!loading && plans.length > 0 && (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="coming-soon-grid">
            {plans.map((p) => (
              <Link
                key={p.id}
                to={`/plans/${p.id}`}
                data-testid={`coming-soon-card-${p.id}`}
                className="group border border-border bg-card hover:border-accent transition-colors overflow-hidden"
              >
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                  {(p.images && p.images[0]) ? (
                    <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" draggable={false} onContextMenu={(e) => e.preventDefault()} data-protected="true" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                      Sketch preview coming
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="overline text-accent">{p.style}</div>
                  <h3 className="font-display text-2xl mt-2 leading-tight">{p.title}</h3>
                  {p.short_description && (
                    <p className="mt-2 text-sm text-muted-foreground italic line-clamp-2">{p.short_description}</p>
                  )}
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <div className="inline-flex items-center gap-2 text-accent">
                      <Clock size={12} /> {timeLeft(p.launch_date) || "Coming soon"}
                    </div>
                    <span className="inline-flex items-center gap-1 group-hover:text-accent transition-colors">
                      Notify me <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ComingSoon;
